import type { Input } from "@/workflow_api/types/workflow";
import type { NotificationNodeData } from "@/schemas/workflow/notification-node";
import { validate } from "uuid";

/**
 * Serializes the notification data into the expected input format.
 * - channel: 'email'
 * - template_id: ID de la plantilla (opcional)
 * - title: título (si no hay template)
 * - description: descripción (si no hay template)
 * - attachment: archivo adjunto (puede repetirse múltiples veces)
 * - recipient: email destino (puede repetirse múltiples veces)
 *
 * Ejemplo:
 * ['channel' => 'email', 'template_id' => '123', 'attachment' => 'doc1.result',
 *  'attachment' => 'doc2.result', 'recipient' => 'user@example.com']
 *
 * @param config - Notification configuration to serialize
 * @returns The serialized notification input
 */
export const getSerializedNotificationInputs = (
  config: NotificationNodeData,
): Input => {
  const inputs: Input = [];

  // Add channel (email)
  inputs.push({
    name: "channel",
    value: "email",
    type: "STRING",
  });

  if (config.template && config.template !== "no-template") {
    inputs.push({
      name: "template_id",
      value: config.template,
      type: "STRING",
    });
  }

  if (!config.template || config.template === "no-template") {
    if (config.title) {
      inputs.push({
        name: "title",
        value: config.title,
        type: "STRING",
      });
    }

    if (config.description) {
      inputs.push({
        name: "description",
        value: config.description,
        type: "STRING",
      });
    }
  }

  // Add attachments - cada uno con el mismo nombre "attachment"
  if (config.attachments && config.attachments.length > 0) {
    config.attachments.forEach((attachment) => {
      inputs.push({
        name: "attachment",
        type: "STRING",
        value: null,
        connection: attachment,
        output_name: "result",
      });
    });
  }

  const validEmails = (config.emails || []).filter(
    (email) => email.value.trim() !== "",
  );
  validEmails.forEach((email) => {
    const isValidUUID = validate(email.field);

    if (!isValidUUID && email.field) {
      inputs.push({
        name: "recipient",
        value: null,
        type: "STRING",
        output_name: email.field,
        connection: email.connection,
      });
    } else if (email.type === "var" && email.global_type) {
      // Variable global: usar value y global_type
      inputs.push({
        name: "recipient",
        value: email.value,
        type: "STRING",
        global_type: email.global_type,
      });
    } else if (email.type === "var" && email.connection) {
      // Variable dinámica: incluir connection y field (si existe)
      const recipientInput: (typeof inputs)[number] = {
        name: "recipient",
        value: null,
        type: "STRING",
        connection: email.connection,
      };
      if (email.field) {
        recipientInput.field = email.field;
      }
      if (email.output_name) {
        recipientInput.output_name = email.output_name;
      }
      inputs.push(recipientInput);
    } else {
      // Input directo o variable legacy
      inputs.push({
        name: "recipient",
        value: email.type === "var" ? `{{${email.value}}}` : email.value,
        type: "STRING",
      });
    }
  });

  // Add template variables - each variable can have multiple entries
  if (config.variables) {
    Object.entries(config.variables).forEach(([varName, varEntries]) => {
      const validEntries = varEntries.filter(
        (entry) => entry.value.trim() !== "",
      );
      validEntries.forEach((entry) => {
        if (entry.type === "var" && entry.global_type) {
          // Variable global: usar value y global_type
          inputs.push({
            name: varName,
            value: entry.value, // nodeId para NODE_EXECUTION_*, nombre propiedad para APPLICANT
            type: entry.var_type,
            global_type: entry.global_type,
          });
        } else if (entry.type === "var" && entry.connection) {
          // Variable dinámica normal: incluir connection y field (si existe)
          const varInput: (typeof inputs)[number] = {
            name: varName,
            value: null,
            type: entry.var_type,
            connection: entry.connection,
          };
          // Solo incluir field si existe (para campos de formulario)
          // Variables de tipo switch/calculated/etc no tienen field
          if (entry.field) {
            varInput.field = entry.field;
          }
          if (entry.output_name) {
            varInput.output_name = entry.output_name;
          }
          inputs.push(varInput);
        } else {
          // Input directo
          inputs.push({
            name: varName,
            value: entry.type === "var" ? `{{${entry.value}}}` : entry.value,
            type: entry.var_type,
          });
        }
      });
    });
  }

  return inputs;
};

/**
 * Deserializes the notification input from the API node.
 * @param inputs - The API node inputs to deserialize
 * @returns The deserialized notification data or null if not found
 */
export const getDeserializedNotificationNode = (
  inputs?: Input,
): NotificationNodeData | null => {
  if (!inputs) return null;

  const getInput = (name: string) => inputs.find((i) => i.name === name)?.value;

  const template = getInput("template_id");
  const title = getInput("title");
  const description = getInput("description");

  if (!template && !title) {
    return null;
  }

  // Obtener los inputs completos de recipient para leer source_connections y global_type
  const recipientInputs = inputs.filter((i) => i.name === "recipient");
  const emails = recipientInputs.map((input) => {
    // Detectar si es una variable global
    const globalType = (input as { global_type?: string }).global_type as
      | "APPLICANT"
      | "NODE_EXECUTION_START"
      | "NODE_EXECUTION_FINISH"
      | undefined;

    // Detectar source_connections (formato del backend)
    const sourceConnection = input.source_connections?.[0];
    const hasSourceConnection = !!sourceConnection;

    // Detectar connection directo
    const hasConnection = !!input.connection;
    const value = input.value as string | null;
    const isVarByFormat =
      typeof value === "string" &&
      value.startsWith("{{") &&
      value.endsWith("}}");
    console.log(input);
    if (globalType) {
      return {
        type: "var" as const,
        value: value as string,
        global_type: globalType,
      };
    } else if (hasSourceConnection) {
      console.log(hasSourceConnection);
      const connectionNodeId = sourceConnection.source_node_id;
      const fieldId =
        sourceConnection.field_id || sourceConnection.field?.id || "";
      const output_name = sourceConnection.output_name;
      console.log(fieldId, output_name);
      if (fieldId && output_name) {
        return {
          type: "var" as const,
          value: output_name,
          connection: connectionNodeId,
          field: fieldId,
          output_name: output_name,
        };
      }
      if (!fieldId && output_name) {
        console.log({
          type: "var" as const,
          value: output_name,
          connection: connectionNodeId,
          output_name: output_name,
        });
        return {
          type: "var" as const,
          value: output_name,
          connection: connectionNodeId,
          output_name: output_name,
        };
      }

      return {
        type: "var" as const,
        value: fieldId,
        connection: connectionNodeId,
        field: fieldId,
      };
    } else if (hasConnection) {
      return {
        type: "var" as const,
        value: input.field || input.connection || "",
        connection: input.connection,
        field: input.field,
      };
    } else if (isVarByFormat) {
      return {
        type: "var" as const,
        value: (value as string).slice(2, -2),
      };
    } else {
      return {
        type: "inp" as const,
        value: value as string,
      };
    }
  });

  // Obtener todos los attachments (múltiples inputs con el mismo nombre "attachment")
  const attachmentInputs = inputs.filter((i) => i.name === "attachment");

  const attachments = attachmentInputs
    .map((input) => {
      const sourceConnection = input.source_connections?.[0];
      if (sourceConnection?.source_node_id) {
        return sourceConnection.source_node_id;
      }
      if (input.connection) {
        return input.connection;
      }
      return input.value as string;
    })
    .filter(Boolean);

  // Deserialize template variables
  // Known input names that are NOT template variables
  const knownInputNames = new Set([
    "channel",
    "template_id",
    "title",
    "description",
    "attachment",
    "recipient",
  ]);

  // Group inputs by name (excluding known inputs)
  const variablesMap: Record<
    string,
    Array<{
      type: "inp" | "var";
      value: string;
      var_type: "STRING" | "DATE" | "FLOAT";
      connection?: string;
      output_name?: string;
      field?: string;
      global_type?:
        | "APPLICANT"
        | "NODE_EXECUTION_START"
        | "NODE_EXECUTION_FINISH";
    }>
  > = {};

  inputs.forEach((input) => {
    if (!knownInputNames.has(input.name)) {
      // This is a template variable
      if (!variablesMap[input.name]) {
        variablesMap[input.name] = [];
      }

      // Detectar si es una variable global
      const globalType = (input as { global_type?: string }).global_type as
        | "APPLICANT"
        | "NODE_EXECUTION_START"
        | "NODE_EXECUTION_FINISH"
        | undefined;

      // Detectar source_connections (formato del backend para variables de formulario)
      const sourceConnection = input.source_connections?.[0];
      const hasSourceConnection = !!sourceConnection;

      // Detectar si es una variable por connection/field directo
      const hasConnection = !!input.connection;
      const value = input.value as string | null;
      const isVarByFormat =
        typeof value === "string" &&
        value.startsWith("{{") &&
        value.endsWith("}}");

      if (globalType) {
        // Variable global
        variablesMap[input.name].push({
          type: "var",
          value: value as string,
          var_type: input.type as "STRING" | "DATE" | "FLOAT",
          global_type: globalType,
        });
      } else if (hasSourceConnection) {
        // Variable con source_connections (formato del backend)
        // source_node_id = connection, field_id o field.id = field
        const connectionNodeId = sourceConnection.source_node_id;
        const fieldId =
          sourceConnection.field_id || sourceConnection.field?.id || "";
        if (fieldId && sourceConnection?.output_name) {
          variablesMap[input.name].push({
            type: "var",
            value: sourceConnection.output_name,
            var_type: input.type as "STRING" | "DATE" | "FLOAT",
            connection: connectionNodeId,
            field: fieldId,
            output_name: sourceConnection.output_name,
          });
        } else if (fieldId) {
          variablesMap[input.name].push({
            type: "var",
            value: fieldId,
            var_type: input.type as "STRING" | "DATE" | "FLOAT",
            connection: connectionNodeId,
            field: fieldId,
          });
        } else if (sourceConnection.output_name) {
          variablesMap[input.name].push({
            type: "var",
            value: sourceConnection.output_name,
            var_type: input.type as "STRING" | "DATE" | "FLOAT",
            connection: connectionNodeId,
            output_name: sourceConnection.output_name,
          });
        }
      } else if (hasConnection) {
        // Variable con connection directo (formato legacy)
        variablesMap[input.name].push({
          type: "var",
          value: input.field || input.connection || "",
          var_type: input.type as "STRING" | "DATE" | "FLOAT",
          connection: input.connection,
          field: input.field,
        });
      } else if (isVarByFormat) {
        // Variable en formato legacy {{}}
        variablesMap[input.name].push({
          type: "var",
          value: (value as string).slice(2, -2),
          var_type: input.type as "STRING" | "DATE" | "FLOAT",
        });
      } else {
        // Input directo
        variablesMap[input.name].push({
          type: "inp",
          value: value as string,
          var_type: input.type as "STRING" | "DATE" | "FLOAT",
        });
      }
    }
  });

  return {
    template: (template as string) || "no-template",
    title: (title as string) || "",
    description: (description as string) || undefined,
    attachments: attachments.length > 0 ? attachments : undefined,
    emails: emails.length > 0 ? emails : [{ type: "inp" as const, value: "" }],
    variables: Object.keys(variablesMap).length > 0 ? variablesMap : undefined,
  };
};
